// Schedule polling helpers for the lightshow client.
//
// Extracted from App.jsx so the re-arm exit conditions (cancel / replace /
// abort) can be unit-tested in isolation. fetchSchedule + interval are
// injectable for tests; production callers use the defaults.

import { API, SCHEDULE_POLL_INTERVAL } from '../config';
import { jitteredInterval } from './pollJitter';
import { apiFetch } from '../deviceId';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Single schedule fetch (no polling). Returns null on a non-OK response.
export async function fetchScheduleOnce(pipeName, fetcher = apiFetch) {
  const res = await fetcher(API.schedule(pipeName));
  if (!res.ok) return null;
  return res.json();
}

// Poll until the show reaches "playing". Returns the playing schedule, or null
// — in which case the caller resets to IDLE and re-arms — when the show is:
//   - cancelled (status === 'idle')
//   - replaced by a different show (showInstanceId !== expectedInstanceId)
//   - aborted by the supervisor (isAborted() becomes true)
// onUpdate fires on each poll (e.g. to surface countdown start).
export async function waitForPlaying(
  pipeName,
  expectedInstanceId,
  isAborted,
  onUpdate,
  {
    fetchSchedule = fetchScheduleOnce,
    interval = SCHEDULE_POLL_INTERVAL,
    getServerTime = null,
  } = {}
) {
  let last = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (isAborted && isAborted()) return null;

    // Start the script the instant the synced clock reaches showStartTime —
    // i.e. exactly when the countdown hits 0 — instead of waiting for the polled
    // 'playing' flip (which lags 0-2s and made the start time vary / the "0"
    // linger). `last` carries showStartTime from the previous poll.
    if (getServerTime && last?.startTime && getServerTime() >= last.startTime) {
      return last;
    }

    // Fetch FIRST (no leading 2s sleep) so an already-/just-playing show is
    // caught immediately rather than after a full poll interval.
    const schedule = await fetchSchedule(pipeName);
    if (!schedule || schedule.status === 'idle') return null;
    if (expectedInstanceId && schedule.showInstanceId !== expectedInstanceId) {
      return null; // a different show was scheduled — bail and re-arm
    }
    if (onUpdate) onUpdate(schedule);
    if (schedule.status === 'playing') return schedule;
    last = schedule;

    // Sleep until showStartTime (precise) or the next poll, whichever is sooner,
    // so the final wake lands on showStartTime rather than a flat 2s boundary.
    const untilStart =
      getServerTime && schedule.startTime
        ? schedule.startTime - getServerTime()
        : interval;
    // Jitter the POLL side only (MM #73). `Math.min` keeps untilStart, so the
    // final sleep still lands exactly on showStartTime and the countdown
    // alignment from 2ffd198b3 is untouched — only the waiting-around polls
    // spread out. This is the busiest poll of all: every client sits here
    // during the countdown.
    await sleep(Math.max(0, Math.min(jitteredInterval(interval), untilStart)));
  }
}
